import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, Users, Store, Tag, TrendingUp, 
  Megaphone, Settings, LogOut, Plus, Eye, EyeOff, 
  Trash2, AlertCircle, CheckCircle, FileText, 
  MessageSquare, Bell, Mail, Phone, Save, User, Lock, KeyRound,
  BarChart3, Send, MousePointer, XCircle, RefreshCw,
  Building2, MapPin, Calendar, Filter, Search, ChevronLeft, ChevronRight,
  Menu, X, Download, QrCode, Cable, Wifi, WifiOff, CreditCard, Bot, Globe, Cake, Zap, FileSignature
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SubscriptionPricesPanel from '../components/SubscriptionPricesPanel';
import { SuperAdminDashboard } from '../components/SuperAdminDashboard';
import { CityAdminPanel } from '../components/CityAdminPanel';
import { CitiesManagement } from '../components/CitiesManagement';
import { StripeConfigPanel } from '../components/StripeConfigPanel';
import { TicketAnalyticsDashboard } from '../components/TicketAnalyticsDashboard';
import { TaxBillingPanel } from '../components/TaxBillingPanel';
import { AdminBirthdayClubSection } from '../components/AdminBirthdayClubSection';
import { AdminReactivationSection } from '../components/AdminReactivationSection';
import { AdminUpsellPanel } from '../components/AdminUpsellPanel';
import { IntegrationsConfigPanel } from '../components/IntegrationsConfigPanel';
import { NotificationBell } from '../components/NotificationBell';
import { AdminLegalAgreementsPanel } from '../components/AdminLegalAgreementsPanel';
import { DemoAnalyticsDashboard } from '../components/DemoAnalyticsDashboard';
import { AdminLandingsPanel } from '../components/AdminLandingsPanel';
import { AdminWebsSolicitudesPanel } from '../components/AdminWebsSolicitudesPanel';
import { TwoFactorSettings } from '../components/TwoFactorSettings';
import { PWAAnalyticsDashboard } from '../components/PWAAnalyticsDashboard';
import { PlatformSettingsPanel } from '../components/PlatformSettingsPanel';
import { FAVICON, LOGO_TRANSPARENT, PIN_ICON_16, PIN_ICON_SVG } from '../assets';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboardData, setDashboardData] = useState(null);
  const [ads, setAds] = useState([]);
  const [settings, setSettings] = useState({});
  const [legalPages, setLegalPages] = useState({
    privacy: { title: 'Política de Privacidad', content: '' },
    terms: { title: 'Términos y Condiciones', content: '' },
    cookies: { title: 'Política de Cookies', content: '' }
  });
  const [supportMessages, setSupportMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAdForm, setShowAdForm] = useState(false);
  const [selectedLegalPage, setSelectedLegalPage] = useState('privacy');
  
  // Email stats state
  const [emailStats, setEmailStats] = useState(null);
  const [loadingEmailStats, setLoadingEmailStats] = useState(false);
  
  // Users/Businesses state
  const [usersData, setUsersData] = useState({ users: [], total: 0, page: 1, pages: 1 });
  const [businessesData, setBusinessesData] = useState({ users: [], total: 0, page: 1, pages: 1 });
  const [userStats, setUserStats] = useState(null);
  const [postalCodes, setPostalCodes] = useState([]);
  const [usersFilter, setUsersFilter] = useState({ search: '', postalCode: '', verifiedOnly: false });
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Campaigns state
  const [campaigns, setCampaigns] = useState([]);
  const [campaignPricing, setCampaignPricing] = useState(null);
  const [showCampaignForm, setShowCampaignForm] = useState(false);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    type: 'email',
    subject: '',
    content: '',
    targetAudience: 'all',
    postalCodes: []
  });
  const [campaignEstimate, setCampaignEstimate] = useState(null);
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Account management state
  const [accountForm, setAccountForm] = useState({
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // Integrations state
  const [integrations, setIntegrations] = useState(null);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  
  const [adForm, setAdForm] = useState({
    position: 'header_carousel',
    slot: 1,
    clientName: '',
    clientEmail: '',
    imageUrl: '',
    linkUrl: '',
    price: 2997,
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    const adminUser = localStorage.getItem('adminUser');
    if (!adminUser) {
      navigate('/nakamadmm-admin');
      return;
    }
    const parsedAdmin = JSON.parse(adminUser);
    setAdmin(parsedAdmin);
    setAccountForm(prev => ({ ...prev, email: parsedAdmin.email || '' }));
    fetchDashboard();
    fetchIntegrations(); // Load integrations on init
  }, [navigate]);

  const getHeaders = () => {
    const adminUser = JSON.parse(localStorage.getItem('adminUser'));
    return { 'X-Admin-Id': adminUser?.id };
  };

  const fetchDashboard = async () => {
    try {
      const [dashRes, adsRes, settingsRes, contactRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/dashboard`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/admin/ads`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/admin/settings`, { headers: getHeaders() }),
        axios.get(`${API_URL}/api/admin/contact-messages`, { headers: getHeaders() }).catch(() => ({ data: { messages: [] } }))
      ]);

      setDashboardData(dashRes.data);
      setAds(adsRes.data.ads || []);
      setSettings(settingsRes.data);
      
      // Load contact messages
      const messages = contactRes.data.messages || [];
      setSupportMessages(messages);
      setUnreadCount(messages.filter(m => !m.read).length);

      // Fetch legal pages
      const [privacyRes, termsRes, cookiesRes] = await Promise.all([
        axios.get(`${API_URL}/api/legal/privacy`),
        axios.get(`${API_URL}/api/legal/terms`),
        axios.get(`${API_URL}/api/legal/cookies`)
      ]);
      setLegalPages({
        privacy: privacyRes.data,
        terms: termsRes.data,
        cookies: cookiesRes.data
      });
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminUser');
    navigate('/nakamadmm-admin');
  };

  const handleCreateAd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/admin/ads`, adForm, { headers: { ...getHeaders(), 'Content-Type': 'application/json' } });
      setShowAdForm(false);
      fetchDashboard();
      toast.success('Anuncio creado');
      setAdForm({ position: 'header_carousel', slot: 1, clientName: '', clientEmail: '', imageUrl: '', linkUrl: '', price: 2997, startDate: '', endDate: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error creando anuncio');
    }
  };

  const toggleAd = async (adId) => {
    try {
      await axios.patch(`${API_URL}/api/admin/ads/${adId}/toggle`, {}, { headers: getHeaders() });
      fetchDashboard();
    } catch (err) {
      console.error('Error toggling ad:', err);
    }
  };

  const deleteAd = async (adId) => {
    if (!window.confirm('¿Eliminar este anuncio?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/ads/${adId}`, { headers: getHeaders() });
      fetchDashboard();
      toast.success('Anuncio eliminado');
    } catch (err) {
      console.error('Error deleting ad:', err);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/admin/settings`, settings, { headers: { ...getHeaders(), 'Content-Type': 'application/json' } });
      toast.success('Configuración guardada');
    } catch (err) {
      toast.error('Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  const saveLegalPage = async (page) => {
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/api/legal/${page}`, legalPages[page], { headers: { ...getHeaders(), 'Content-Type': 'application/json' } });
      toast.success('Página legal guardada');
    } catch (err) {
      console.error('Error guardando página legal:', err);
      toast.error('Error guardando página legal: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const markSupportRead = async (messageId) => {
    try {
      await axios.patch(`${API_URL}/api/admin/support/${messageId}/read`, {}, { headers: getHeaders() });
      fetchDashboard();
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const replySupportMessage = async (messageId, reply) => {
    try {
      await axios.patch(`${API_URL}/api/admin/support/${messageId}/reply`, { reply }, { headers: { ...getHeaders(), 'Content-Type': 'application/json' } });
      toast.success('Respuesta enviada');
      fetchDashboard();
    } catch (err) {
      toast.error('Error enviando respuesta');
    }
  };

  const saveAccountChanges = async () => {
    // Validate password confirmation
    if (accountForm.newPassword && accountForm.newPassword !== accountForm.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {};
      if (accountForm.email && accountForm.email !== admin.email) {
        payload.email = accountForm.email;
      }
      if (accountForm.newPassword) {
        payload.currentPassword = accountForm.currentPassword;
        payload.newPassword = accountForm.newPassword;
      }
      
      if (Object.keys(payload).length === 0) {
        toast.info('No hay cambios para guardar');
        setSaving(false);
        return;
      }
      
      const response = await axios.patch(`${API_URL}/api/admin/account`, payload, { 
        headers: { ...getHeaders(), 'Content-Type': 'application/json' } 
      });
      
      if (response.data.admin) {
        localStorage.setItem('adminUser', JSON.stringify(response.data.admin));
        setAdmin(response.data.admin);
      }
      
      // Clear password fields
      setAccountForm(prev => ({ 
        ...prev, 
        currentPassword: '', 
        newPassword: '', 
        confirmPassword: '' 
      }));
      
      toast.success('Cuenta actualizada correctamente');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error actualizando cuenta');
    } finally {
      setSaving(false);
    }
  };

  const fetchEmailStats = async () => {
    setLoadingEmailStats(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/email-stats?days=30`, { headers: getHeaders() });
      setEmailStats(response.data);
    } catch (err) {
      console.error('Error fetching email stats:', err);
    } finally {
      setLoadingEmailStats(false);
    }
  };

  const generateTestStats = async () => {
    try {
      await axios.post(`${API_URL}/api/admin/email-stats/test`, {}, { headers: getHeaders() });
      toast.success('Datos de prueba generados');
      fetchEmailStats();
    } catch (err) {
      toast.error('Error generando datos de prueba');
    }
  };

  const fetchIntegrations = async () => {
    setLoadingIntegrations(true);
    try {
      const response = await axios.get(`${API_URL}/api/integrations/status`);
      setIntegrations(response.data);
    } catch (err) {
      console.error('Error fetching integrations:', err);
      toast.error('Error verificando integraciones');
    } finally {
      setLoadingIntegrations(false);
    }
  };

  const fetchUsers = async (role, page = 1) => {
    setLoadingUsers(true);
    try {
      const params = new URLSearchParams({
        role,
        page: page.toString(),
        limit: '20'
      });
      if (usersFilter.postalCode) params.append('postal_code', usersFilter.postalCode);
      if (usersFilter.verifiedOnly) params.append('verified_only', 'true');
      
      const response = await axios.get(`${API_URL}/api/admin/users?${params}`, { headers: getHeaders() });
      
      if (role === 'customer') {
        setUsersData(response.data);
      } else {
        setBusinessesData(response.data);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      toast.error('Error cargando usuarios');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchUserStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/stats`, { headers: getHeaders() });
      setUserStats(response.data);
    } catch (err) {
      console.error('Error fetching user stats:', err);
    }
  };

  const fetchPostalCodes = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/postal-codes`, { headers: getHeaders() });
      setPostalCodes(response.data.postalCodes || []);
    } catch (err) {
      console.error('Error fetching postal codes:', err);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/campaigns`, { headers: getHeaders() });
      setCampaigns(response.data.campaigns || []);
    } catch (err) {
      console.error('Error fetching campaigns:', err);
    }
  };

  const fetchCampaignPricing = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/campaigns/pricing`, { headers: getHeaders() });
      setCampaignPricing(response.data);
    } catch (err) {
      console.error('Error fetching pricing:', err);
    }
  };

  const estimateCampaign = async () => {
    try {
      const response = await axios.post(`${API_URL}/api/admin/campaigns/estimate`, campaignForm, { 
        headers: { ...getHeaders(), 'Content-Type': 'application/json' } 
      });
      setCampaignEstimate(response.data);
    } catch (err) {
      toast.error('Error estimando campaña');
    }
  };

  const createCampaign = async () => {
    if (!campaignForm.name || !campaignForm.content) {
      toast.error('Completa todos los campos obligatorios');
      return;
    }
    
    setSaving(true);
    try {
      const response = await axios.post(`${API_URL}/api/admin/campaigns/create`, campaignForm, { 
        headers: { ...getHeaders(), 'Content-Type': 'application/json' } 
      });
      toast.success(`Campaña creada: ${response.data.recipientCount} destinatarios`);
      setShowCampaignForm(false);
      setCampaignForm({ name: '', type: 'email', subject: '', content: '', targetAudience: 'all', postalCodes: [] });
      setCampaignEstimate(null);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error creando campaña');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const pricing = dashboardData?.adPricing || {};

  // Check if admin is superadmin (has full access) or subadmin (needs permission checks)
  const isSuperAdmin = !admin?.role || admin?.role === 'superadmin';
  const subAdminPermissions = admin?.permissions || {};
  
  // Helper to check if a tab should be visible for the current admin
  const canAccessTab = (tabId) => {
    // Superadmins can access everything
    if (isSuperAdmin) return true;
    
    // Basic tabs always visible to all admins
    const alwaysVisibleTabs = ['dashboard', 'account'];
    if (alwaysVisibleTabs.includes(tabId)) return true;
    
    // Permission-based tabs for sub-admins
    const tabPermissions = {
      'webs': subAdminPermissions.can_access_webs_demo,
      'upsell': subAdminPermissions.can_send_upsell_messages,
      'metrics': subAdminPermissions.can_view_revenue_metrics,
      'businesses': subAdminPermissions.can_manage_assigned_businesses,
    };
    
    // If tab has explicit permission mapping, check it
    if (tabId in tabPermissions) {
      return tabPermissions[tabId] === true;
    }
    
    // Other tabs require superadmin by default
    return false;
  };

  const allNavItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { id: 'metrics', icon: Globe, label: 'Métricas Globales' },
    { id: 'pwa-analytics', icon: BarChart3, label: 'PWA Analytics' },
    { id: 'webs', icon: Globe, label: 'Webs Demo' },
    { id: 'reactivation', icon: Zap, label: 'Reactivación IA' },
    { id: 'demo-analytics', icon: BarChart3, label: 'Analíticas Demo' },
    { id: 'landings', icon: Cable, label: 'Landings & Citas' },
    { id: 'upsell', icon: TrendingUp, label: 'Upsell & Consultores' },
    { id: 'birthday-club', icon: Cake, label: 'Club Cumpleaños' },
    { id: 'stripe', icon: CreditCard, label: 'Stripe' },
    { id: 'cities', icon: MapPin, label: 'Ciudades' },
    { id: 'users', icon: Users, label: 'Usuarios' },
    { id: 'businesses', icon: Building2, label: 'Negocios' },
    { id: 'subscriptions', icon: CreditCard, label: 'Suscripciones' },
    { id: 'tax-billing', icon: FileText, label: 'Impuestos' },
    { id: 'campaigns', icon: Send, label: 'Campañas' },
    { id: 'email-stats', icon: BarChart3, label: 'Email Stats' },
    { id: 'integrations', icon: Cable, label: 'Integraciones' },
    { id: 'ads', icon: Megaphone, label: 'Publicidad' },
    { id: 'support', icon: MessageSquare, label: 'Soporte', badge: unreadCount },
    { id: 'legal-agreements', icon: FileSignature, label: 'Acuerdos Legales' },
    { id: 'legal', icon: FileText, label: 'Textos Legales' },
    { id: 'platform-settings', icon: Building2, label: 'Datos Empresa' },
    { id: 'settings', icon: Settings, label: 'Configuración' },
    { id: 'account', icon: User, label: 'Mi Cuenta' }
  ];
  
  // Filter nav items based on permissions
  const navItems = allNavItems.filter(item => canAccessTab(item.id));

  const handleNavClick = (tabId) => {
    setActiveTab(tabId);
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-900 flex flex-col lg:flex-row">
      {/* Mobile Header */}
      <div className="lg:hidden bg-gray-800 border-b border-gray-700 px-4 py-3 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">LG</span>
          </div>
          <span className="text-white font-semibold">Admin</span>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell adminId={admin?.id} />
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 text-gray-400 hover:text-white"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 top-[57px] bg-gray-900/95 z-40 overflow-y-auto">
          <nav className="p-4 space-y-1">
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-colors ${
                  activeTab === item.id 
                    ? 'bg-emerald-600/20 text-emerald-400' 
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </div>
                {item.badge > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{item.badge}</span>
                )}
              </button>
            ))}
            <div className="pt-4 mt-4 border-t border-gray-700">
              <p className="text-xs text-gray-500 px-4 mb-2">{admin?.email}</p>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-colors"
              >
                <LogOut className="w-5 h-5" />
                Cerrar Sesión
              </button>
            </div>
          </nav>
        </div>
      )}

      {/* Desktop Sidebar */}
      <div className="hidden lg:flex w-64 bg-gray-800 border-r border-gray-700 p-4 flex-col flex-shrink-0">
        <div className="flex items-center justify-between mb-8 px-2">
          <div className="flex items-center gap-3">
            <img 
              src={FAVICON} 
              alt="LokAlGrow" 
              className="w-10 h-10 rounded-lg object-contain"
            />
            <div>
              <p className="text-xs text-gray-400">Admin Panel</p>
            </div>
          </div>
          <NotificationBell adminId={admin?.id} />
        </div>

        <nav className="flex-1 space-y-1">
          {navItems.map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors ${
                activeTab === item.id 
                  ? 'bg-emerald-600/20 text-emerald-400' 
                  : 'text-gray-400 hover:bg-gray-700/50 hover:text-white'
              }`}
            >
              <div className="flex items-center gap-3">
                <item.icon className="w-5 h-5" />
                {item.label}
              </div>
              {item.badge > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{item.badge}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="pt-4 border-t border-gray-700">
          <p className="text-xs text-gray-500 px-3 mb-2">{admin?.email}</p>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Cerrar Sesión
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">Dashboard</h2>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
              {[
                { label: 'Usuarios', value: stats.totalUsers || 0, icon: Users, color: 'blue' },
                { label: 'Negocios', value: stats.totalBusinesses || 0, icon: Store, color: 'emerald' },
                { label: 'Kupons', value: stats.activeKupons || 0, icon: Tag, color: 'purple' },
                { label: 'Ingresos', value: `€${stats.totalRevenue || 0}`, icon: TrendingUp, color: 'amber' }
              ].map((stat, i) => (
                <div key={i} className="bg-gray-800 rounded-xl p-3 sm:p-5 border border-gray-700">
                  <div className="flex items-center justify-between mb-2 sm:mb-3">
                    <span className="text-gray-400 text-xs sm:text-sm">{stat.label}</span>
                    <stat.icon className={`w-4 h-4 sm:w-5 sm:h-5 text-${stat.color}-400`} />
                  </div>
                  <p className="text-lg sm:text-2xl font-bold text-white">{stat.value}</p>
                </div>
              ))}
            </div>

            <div className="bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-700 mb-6 sm:mb-8">
              <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4 flex items-center gap-2">
                <Megaphone className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
                Espacios Publicitarios
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                {Object.entries(pricing).map(([key, info]) => (
                  <div key={key} className="bg-gray-700/50 rounded-lg p-3 sm:p-4">
                    <p className="text-white font-medium text-sm sm:text-base">{info.name}</p>
                    <p className="text-emerald-400 text-lg sm:text-xl font-bold">€{info.price}/mes</p>
                    <p className="text-gray-400 text-xs sm:text-sm">{info.slots} slot(s)</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-800 rounded-xl p-4 sm:p-6 border border-gray-700">
              <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Últimos Intentos de Login</h3>
              <div className="space-y-2 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                {(dashboardData?.recentLoginAttempts || []).slice(0, 5).map((attempt, i) => (
                  <div key={i} className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-2 border-b border-gray-700 last:border-0 gap-1 sm:gap-0">
                    <div className="flex items-center gap-2 sm:gap-3">
                      {attempt.success ? <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />}
                      <span className="text-gray-300 text-xs sm:text-sm break-all">{attempt.email}</span>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-4 text-xs text-gray-500 ml-6 sm:ml-0">
                      <span>{attempt.ip}</span>
                      <span className="hidden sm:inline">{new Date(attempt.timestamp).toLocaleString()}</span>
                      <span className="sm:hidden">{new Date(attempt.timestamp).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Global Metrics Tab (Super Admin Dashboard) */}
        {activeTab === 'metrics' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-2xl font-bold text-white mb-6">Métricas Globales de la Plataforma</h2>
            <SuperAdminDashboard adminId={admin?.id} />
            
            {/* Ticket Verification Analytics */}
            <div className="mt-8">
              <h3 className="text-xl font-bold text-white mb-4">Analytics de Verificación de Tickets</h3>
              <TicketAnalyticsDashboard isAdmin={true} />
            </div>
          </motion.div>
        )}

        {/* Stripe Configuration Tab */}
        {activeTab === 'stripe' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-2xl font-bold text-white mb-6">Configuración de Stripe</h2>
            <div className="max-w-2xl">
              <StripeConfigPanel adminId={admin?.id} />
            </div>
          </motion.div>
        )}

        {/* PWA Analytics Tab */}
        {activeTab === 'pwa-analytics' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <PWAAnalyticsDashboard adminId={admin?.id} />
          </motion.div>
        )}

        {/* Birthday Club Tab */}
        {activeTab === 'birthday-club' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <AdminBirthdayClubSection />
          </motion.div>
        )}

        {/* Reactivation AI Tab */}
        {activeTab === 'reactivation' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <AdminReactivationSection />
          </motion.div>
        )}

        {/* Demo Analytics Tab */}
        {activeTab === 'demo-analytics' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <DemoAnalyticsDashboard />
          </motion.div>
        )}

        {/* Landings & Appointments Tab */}
        {activeTab === 'landings' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <AdminLandingsPanel />
          </motion.div>
        )}

        {/* Webs Solicitudes Tab */}
        {activeTab === 'webs' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <AdminWebsSolicitudesPanel adminId={admin?.id} isSuperAdmin={isSuperAdmin} />
          </motion.div>
        )}

        {/* Upsell & Consultores Tab */}
        {activeTab === 'upsell' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-2xl font-bold text-white mb-6">Upsell & Gestión de Consultores</h2>
            <AdminUpsellPanel adminId={admin?.id} />
          </motion.div>
        )}

        {/* Cities Tab - Tenant Management */}
        {activeTab === 'cities' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-2xl font-bold text-white mb-6">Gestión de Ciudades</h2>
            <CitiesManagement adminId={admin?.id} />
          </motion.div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} onAnimationComplete={() => { if (!usersData.users.length) { fetchUsers('customer'); fetchUserStats(); fetchPostalCodes(); } }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Usuarios (Clientes)</h2>
              <button onClick={() => fetchUsers('customer')} disabled={loadingUsers} className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm">
                <RefreshCw className={`w-4 h-4 ${loadingUsers ? 'animate-spin' : ''}`} /> Actualizar
              </button>
            </div>

            {/* User Stats */}
            {userStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm">Total Clientes</p>
                  <p className="text-2xl font-bold text-white">{userStats.totalCustomers}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm">Tel. Verificados</p>
                  <p className="text-2xl font-bold text-emerald-400">{userStats.verifiedPhone}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm">SMS Opt-in</p>
                  <p className="text-2xl font-bold text-blue-400">{userStats.smsOptIn}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm">Email Opt-in</p>
                  <p className="text-2xl font-bold text-purple-400">{userStats.emailOptIn}</p>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-6">
              <select value={usersFilter.postalCode} onChange={(e) => setUsersFilter({...usersFilter, postalCode: e.target.value})} className="px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm">
                <option value="">Todos los C.P.</option>
                {postalCodes.map(pc => (
                  <option key={pc._id} value={pc._id}>{pc._id} ({pc.totalUsers})</option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-gray-300 text-sm">
                <input type="checkbox" checked={usersFilter.verifiedOnly} onChange={(e) => setUsersFilter({...usersFilter, verifiedOnly: e.target.checked})} className="rounded" />
                Solo verificados
              </label>
              <button onClick={() => fetchUsers('customer')} className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm">
                <Filter className="w-4 h-4 inline mr-1" /> Filtrar
              </button>
            </div>

            {/* Users Table */}
            <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Nombre</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Teléfono</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">C.P.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Verificado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Fecha Registro</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {usersData.users.map((user, i) => (
                    <tr key={i} className="hover:bg-gray-700/30">
                      <td className="px-4 py-3 text-sm text-white">{user.name} {user.surname}</td>
                      <td className="px-4 py-3 text-sm text-gray-300">{user.email}</td>
                      <td className="px-4 py-3 text-sm text-gray-300">{user.phone || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-300">{user.postalCode || '-'}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {user.phoneVerified && <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded">Tel</span>}
                          {user.emailVerified && <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded">Email</span>}
                          {!user.phoneVerified && !user.emailVerified && <span className="text-gray-500 text-xs">No</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {usersData.users.length === 0 && (
                <div className="py-8 text-center text-gray-400">No hay usuarios</div>
              )}
            </div>

            {/* Pagination */}
            {usersData.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <button onClick={() => fetchUsers('customer', usersData.page - 1)} disabled={usersData.page <= 1} className="p-2 bg-gray-700 rounded-lg disabled:opacity-50">
                  <ChevronLeft className="w-4 h-4 text-white" />
                </button>
                <span className="text-gray-400 text-sm">Página {usersData.page} de {usersData.pages}</span>
                <button onClick={() => fetchUsers('customer', usersData.page + 1)} disabled={usersData.page >= usersData.pages} className="p-2 bg-gray-700 rounded-lg disabled:opacity-50">
                  <ChevronRight className="w-4 h-4 text-white" />
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* Businesses Tab */}
        {activeTab === 'businesses' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} onAnimationComplete={() => { if (!businessesData.users.length) { fetchUsers('business'); fetchUserStats(); fetchPostalCodes(); } }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Negocios Registrados</h2>
              <button onClick={() => fetchUsers('business')} disabled={loadingUsers} className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm">
                <RefreshCw className={`w-4 h-4 ${loadingUsers ? 'animate-spin' : ''}`} /> Actualizar
              </button>
            </div>

            {/* Business Stats */}
            {userStats && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm">Total Negocios</p>
                  <p className="text-2xl font-bold text-white">{userStats.totalBusinesses}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm">SMS Opt-in</p>
                  <p className="text-2xl font-bold text-blue-400">{userStats.smsOptIn}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                  <p className="text-gray-400 text-sm">Email Opt-in</p>
                  <p className="text-2xl font-bold text-purple-400">{userStats.emailOptIn}</p>
                </div>
              </div>
            )}

            {/* Businesses Table - Mobile Cards, Desktop Table */}
            <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
              {/* Mobile Card View */}
              <div className="block md:hidden divide-y divide-gray-700">
                {businessesData.users.map((biz, i) => (
                  <div key={i} className="p-4 hover:bg-gray-700/30">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="text-sm text-white font-medium">{biz.businessName || biz.name}</p>
                        <p className="text-xs text-gray-400">{biz.businessType || '-'}</p>
                      </div>
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        biz.tier === 'premium' ? 'bg-amber-500/20 text-amber-400' :
                        biz.tier === 'credits' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>{biz.tier || 'free'}</span>
                    </div>
                    <p className="text-xs text-gray-300 break-all mb-1">{biz.email}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-400">C.P. {biz.postalCode || '-'}</span>
                      <a 
                        href={`${API_URL}/api/business/${biz.id}/qr-flyer`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 px-2 py-1 bg-emerald-600/20 text-emerald-400 text-xs rounded hover:bg-emerald-600/30 transition-colors"
                      >
                        <Download className="w-3 h-3" />
                        PDF
                      </a>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-700/50">
                  <tr>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Negocio</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Tipo</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Email</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase hidden lg:table-cell">Teléfono</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">C.P.</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Tier</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">QR Flyer</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {businessesData.users.map((biz, i) => (
                    <tr key={i} className="hover:bg-gray-700/30">
                      <td className="px-3 sm:px-4 py-3 text-sm text-white font-medium">{biz.businessName || biz.name}</td>
                      <td className="px-3 sm:px-4 py-3 text-sm text-gray-300">{biz.businessType || '-'}</td>
                      <td className="px-3 sm:px-4 py-3 text-sm text-gray-300 truncate max-w-[200px]">{biz.email}</td>
                      <td className="px-3 sm:px-4 py-3 text-sm text-gray-300 hidden lg:table-cell">{biz.phone || '-'}</td>
                      <td className="px-3 sm:px-4 py-3 text-sm text-gray-300">{biz.postalCode || '-'}</td>
                      <td className="px-3 sm:px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          biz.tier === 'premium' ? 'bg-amber-500/20 text-amber-400' :
                          biz.tier === 'credits' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>{biz.tier || 'free'}</span>
                      </td>
                      <td className="px-3 sm:px-4 py-3">
                        <a 
                          href={`${API_URL}/api/business/${biz.id}/qr-flyer`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 px-2 py-1 bg-emerald-600/20 text-emerald-400 text-xs rounded hover:bg-emerald-600/30 transition-colors"
                        >
                          <Download className="w-3 h-3" />
                          <span>PDF</span>
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {businessesData.users.length === 0 && (
                <div className="py-8 text-center text-gray-400">No hay negocios registrados</div>
              )}
            </div>

            {/* Pagination */}
            {businessesData.pages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <button onClick={() => fetchUsers('business', businessesData.page - 1)} disabled={businessesData.page <= 1} className="p-2 bg-gray-700 rounded-lg disabled:opacity-50">
                  <ChevronLeft className="w-4 h-4 text-white" />
                </button>
                <span className="text-gray-400 text-sm">Página {businessesData.page} de {businessesData.pages}</span>
                <button onClick={() => fetchUsers('business', businessesData.page + 1)} disabled={businessesData.page >= businessesData.pages} className="p-2 bg-gray-700 rounded-lg disabled:opacity-50">
                  <ChevronRight className="w-4 h-4 text-white" />
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* Subscriptions Tab - NEW */}
        {activeTab === 'subscriptions' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Gestión de Suscripciones B2B</h2>
            </div>
            <SubscriptionPricesPanel getHeaders={getHeaders} />
          </motion.div>
        )}

        {/* Tax & Billing Tab */}
        {activeTab === 'tax-billing' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <TaxBillingPanel getHeaders={getHeaders} />
          </motion.div>
        )}

        {/* Campaigns Tab */}
        {activeTab === 'campaigns' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} onAnimationComplete={() => { fetchCampaigns(); fetchCampaignPricing(); fetchPostalCodes(); }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Campañas de Marketing</h2>
              <button onClick={() => setShowCampaignForm(true)} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors">
                <Plus className="w-5 h-5" /> Nueva Campaña
              </button>
            </div>

            {/* Pricing Info */}
            {campaignPricing && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                  <h3 className="text-white font-semibold mb-2 flex items-center gap-2"><Mail className="w-4 h-4 text-emerald-400" /> Email</h3>
                  <p className="text-2xl font-bold text-emerald-400">€{campaignPricing.email.perMessage}/email</p>
                  <p className="text-gray-400 text-sm">Mínimo: €{campaignPricing.email.minimumOrder}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                  <h3 className="text-white font-semibold mb-2 flex items-center gap-2"><Phone className="w-4 h-4 text-blue-400" /> SMS</h3>
                  <p className="text-2xl font-bold text-blue-400">€{campaignPricing.sms.perMessage}/SMS</p>
                  <p className="text-gray-400 text-sm">Mínimo: €{campaignPricing.sms.minimumOrder}</p>
                </div>
              </div>
            )}

            {/* Campaign Form Modal */}
            {showCampaignForm && (
              <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-gray-800 rounded-xl p-6 w-full max-w-lg border border-gray-700 max-h-[90vh] overflow-y-auto">
                  <h3 className="text-xl font-bold text-white mb-4">Nueva Campaña</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Nombre de la campaña</label>
                      <input type="text" value={campaignForm.name} onChange={(e) => setCampaignForm({...campaignForm, name: e.target.value})} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" placeholder="Ej: Promoción Navidad" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Tipo</label>
                        <select value={campaignForm.type} onChange={(e) => setCampaignForm({...campaignForm, type: e.target.value})} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white">
                          <option value="email">Email</option>
                          <option value="sms">SMS</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Audiencia</label>
                        <select value={campaignForm.targetAudience} onChange={(e) => setCampaignForm({...campaignForm, targetAudience: e.target.value})} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white">
                          <option value="all">Todos</option>
                          <option value="customers">Solo Clientes</option>
                          <option value="businesses">Solo Negocios</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Filtrar por Código Postal</label>
                      <select multiple value={campaignForm.postalCodes} onChange={(e) => setCampaignForm({...campaignForm, postalCodes: Array.from(e.target.selectedOptions, opt => opt.value)})} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white h-24">
                        {postalCodes.map(pc => (
                          <option key={pc._id} value={pc._id}>{pc._id} ({pc.totalUsers} usuarios)</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">Mantén Ctrl para seleccionar varios. Vacío = todos.</p>
                    </div>
                    {campaignForm.type === 'email' && (
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Asunto del email</label>
                        <input type="text" value={campaignForm.subject} onChange={(e) => setCampaignForm({...campaignForm, subject: e.target.value})} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" placeholder="Ej: ¡Nuevas ofertas en tu zona!" />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Contenido del mensaje</label>
                      <textarea value={campaignForm.content} onChange={(e) => setCampaignForm({...campaignForm, content: e.target.value})} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white h-32" placeholder={campaignForm.type === 'sms' ? 'Máximo 160 caracteres para SMS' : 'Escribe tu mensaje...'} />
                      {campaignForm.type === 'sms' && <p className="text-xs text-gray-500 mt-1">{campaignForm.content.length}/160 caracteres</p>}
                    </div>
                    
                    <button onClick={estimateCampaign} className="w-full py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg">
                      Calcular Coste
                    </button>
                    
                    {campaignEstimate && (
                      <div className="bg-emerald-900/30 border border-emerald-700 rounded-lg p-4">
                        <p className="text-emerald-400 font-semibold">Estimación:</p>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                          <span className="text-gray-400">Destinatarios:</span>
                          <span className="text-white font-semibold">{campaignEstimate.recipientCount}</span>
                          <span className="text-gray-400">Coste por mensaje:</span>
                          <span className="text-white">€{campaignEstimate.perMessageCost}</span>
                          <span className="text-gray-400">Coste total:</span>
                          <span className="text-emerald-400 font-bold text-lg">€{campaignEstimate.totalCost.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex gap-3 pt-4">
                      <button onClick={() => { setShowCampaignForm(false); setCampaignEstimate(null); }} className="flex-1 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg">Cancelar</button>
                      <button onClick={createCampaign} disabled={saving || !campaignForm.name || !campaignForm.content} className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-600/50 text-white rounded-lg">
                        {saving ? 'Creando...' : 'Crear Campaña'}
                      </button>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}

            {/* Campaigns List */}
            <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Nombre</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Tipo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Audiencia</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Destinatarios</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Coste</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Estado</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Fecha</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {campaigns.map((camp, i) => (
                    <tr key={i} className="hover:bg-gray-700/30">
                      <td className="px-4 py-3 text-sm text-white font-medium">{camp.name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs rounded ${camp.type === 'email' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}`}>{camp.type.toUpperCase()}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">{camp.targetAudience}</td>
                      <td className="px-4 py-3 text-sm text-white">{camp.recipientCount}</td>
                      <td className="px-4 py-3 text-sm text-emerald-400">€{camp.totalCost?.toFixed(2) || '0.00'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs rounded ${
                          camp.status === 'sent' ? 'bg-emerald-500/20 text-emerald-400' :
                          camp.status === 'queued' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>{camp.status}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">{camp.createdAt ? new Date(camp.createdAt).toLocaleDateString() : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {campaigns.length === 0 && (
                <div className="py-8 text-center text-gray-400">No hay campañas creadas</div>
              )}
            </div>
          </motion.div>
        )}

        {/* Email Stats Tab */}
        {activeTab === 'email-stats' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} onAnimationComplete={() => !emailStats && fetchEmailStats()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Estadísticas de Email</h2>
              <div className="flex items-center gap-2">
                <button 
                  onClick={generateTestStats} 
                  className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors text-sm"
                >
                  <Plus className="w-4 h-4" /> Datos Demo
                </button>
                <button 
                  onClick={fetchEmailStats} 
                  disabled={loadingEmailStats}
                  className="flex items-center gap-2 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors text-sm"
                >
                  <RefreshCw className={`w-4 h-4 ${loadingEmailStats ? 'animate-spin' : ''}`} /> Actualizar
                </button>
              </div>
            </div>

            {loadingEmailStats ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : emailStats ? (
              <div className="space-y-6">
                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-gray-400 text-sm">Emails Enviados</span>
                      <Send className="w-5 h-5 text-blue-400" />
                    </div>
                    <p className="text-2xl font-bold text-white">{emailStats.totals?.sent || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">Últimos 30 días</p>
                  </div>
                  <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-gray-400 text-sm">Tasa de Apertura</span>
                      <Eye className="w-5 h-5 text-emerald-400" />
                    </div>
                    <p className="text-2xl font-bold text-white">{emailStats.rates?.openRate || 0}%</p>
                    <p className="text-xs text-gray-500 mt-1">{emailStats.totals?.uniqueOpened || 0} abiertos</p>
                  </div>
                  <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-gray-400 text-sm">Tasa de Clics</span>
                      <MousePointer className="w-5 h-5 text-purple-400" />
                    </div>
                    <p className="text-2xl font-bold text-white">{emailStats.rates?.clickRate || 0}%</p>
                    <p className="text-xs text-gray-500 mt-1">{emailStats.totals?.clicked || 0} clics</p>
                  </div>
                  <div className="bg-gray-800 rounded-xl p-5 border border-gray-700">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-gray-400 text-sm">Tasa de Rebote</span>
                      <XCircle className="w-5 h-5 text-red-400" />
                    </div>
                    <p className="text-2xl font-bold text-white">{emailStats.rates?.bounceRate || 0}%</p>
                    <p className="text-xs text-gray-500 mt-1">{emailStats.totals?.bounced || 0} rebotes</p>
                  </div>
                </div>

                {/* Delivery Rate Bar */}
                <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4">Tasa de Entrega</h3>
                  <div className="flex items-center gap-4">
                    <div className="flex-1 bg-gray-700 rounded-full h-4 overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all duration-500"
                        style={{ width: `${emailStats.rates?.deliveryRate || 0}%` }}
                      />
                    </div>
                    <span className="text-2xl font-bold text-emerald-400 w-20 text-right">{emailStats.rates?.deliveryRate || 0}%</span>
                  </div>
                  <p className="text-gray-400 text-sm mt-2">
                    {emailStats.totals?.delivered || 0} entregados de {emailStats.totals?.sent || 0} enviados
                  </p>
                </div>

                {/* Daily Stats Chart (Simple Bar) */}
                <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4">Actividad Diaria (Últimos 7 días)</h3>
                  <div className="flex items-end gap-2 h-40">
                    {(emailStats.dailyStats || []).slice(0, 7).reverse().map((day, i) => {
                      const maxSent = Math.max(...(emailStats.dailyStats || []).map(d => d.sent || 0), 1);
                      const height = ((day.sent || 0) / maxSent) * 100;
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-2">
                          <div className="w-full flex flex-col items-center">
                            <span className="text-xs text-gray-400 mb-1">{day.sent || 0}</span>
                            <div 
                              className="w-full bg-gradient-to-t from-emerald-600 to-emerald-400 rounded-t-lg transition-all duration-300 hover:from-emerald-500 hover:to-emerald-300"
                              style={{ height: `${Math.max(height, 5)}%`, minHeight: '8px' }}
                            />
                          </div>
                          <span className="text-xs text-gray-500">{day.date?.slice(5) || ''}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Recent Events */}
                <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4">Eventos Recientes</h3>
                  {(emailStats.recentEvents || []).length === 0 ? (
                    <div className="text-center py-8">
                      <Mail className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <p className="text-gray-400">No hay eventos de email registrados</p>
                      <p className="text-gray-500 text-sm mt-1">Los eventos aparecerán aquí cuando configures el webhook de Brevo</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {emailStats.recentEvents.map((event, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-gray-700 last:border-0">
                          <div className="flex items-center gap-3">
                            {event.event === 'delivered' && <CheckCircle className="w-4 h-4 text-emerald-400" />}
                            {event.event === 'opened' && <Eye className="w-4 h-4 text-blue-400" />}
                            {event.event === 'clicked' && <MousePointer className="w-4 h-4 text-purple-400" />}
                            {event.event === 'bounced' && <XCircle className="w-4 h-4 text-red-400" />}
                            {!['delivered', 'opened', 'clicked', 'bounced'].includes(event.event) && <Mail className="w-4 h-4 text-gray-400" />}
                            <div>
                              <span className="text-gray-300 text-sm">{event.email}</span>
                              <span className={`ml-2 text-xs px-2 py-0.5 rounded ${
                                event.event === 'delivered' ? 'bg-emerald-500/20 text-emerald-400' :
                                event.event === 'opened' ? 'bg-blue-500/20 text-blue-400' :
                                event.event === 'clicked' ? 'bg-purple-500/20 text-purple-400' :
                                event.event === 'bounced' ? 'bg-red-500/20 text-red-400' :
                                'bg-gray-500/20 text-gray-400'
                              }`}>{event.event}</span>
                            </div>
                          </div>
                          <span className="text-xs text-gray-500">{new Date(event.timestamp).toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Webhook Setup Info */}
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-blue-400 font-medium">Configurar Webhooks de Brevo</p>
                      <p className="text-blue-400/70 text-sm mt-1">
                        Para recibir eventos en tiempo real, configura el webhook en Brevo:
                      </p>
                      <ol className="text-blue-400/70 text-sm mt-2 list-decimal list-inside space-y-1">
                        <li>Ve a <a href="https://app.brevo.com/settings/transactional/webhooks" target="_blank" rel="noopener noreferrer" className="text-blue-400 underline">Brevo → Settings → Webhooks</a></li>
                        <li>Crea un nuevo webhook con la URL: <code className="bg-blue-500/20 px-1 rounded">{API_URL}/api/webhooks/brevo</code></li>
                        <li>Selecciona los eventos: delivered, opened, clicked, bounced, spam</li>
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No hay estadísticas disponibles</p>
                <button 
                  onClick={generateTestStats}
                  className="mt-4 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                >
                  Generar Datos de Prueba
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* Integrations Tab */}
        {activeTab === 'integrations' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="integrations-tab">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Estado de Integraciones</h2>
              <button 
                onClick={fetchIntegrations} 
                disabled={loadingIntegrations}
                className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                <RefreshCw className={`w-5 h-5 ${loadingIntegrations ? 'animate-spin' : ''}`} />
                Actualizar
              </button>
            </div>

            {loadingIntegrations ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
              </div>
            ) : integrations ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Stripe */}
                <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${integrations.stripe?.configured ? 'bg-indigo-600' : 'bg-gray-700'}`}>
                        <CreditCard className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Stripe</h3>
                        <p className="text-sm text-gray-400">Pasarela de pagos</p>
                      </div>
                    </div>
                    {integrations.stripe?.configured ? (
                      <span className="flex items-center gap-1 px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm">
                        <Wifi className="w-4 h-4" /> Conectado
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm">
                        <WifiOff className="w-4 h-4" /> No configurado
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-400">
                    {integrations.stripe?.configured ? (
                      <div className="space-y-3">
                        <p>La clave API de Stripe está configurada. Los pagos están habilitados.</p>
                        <button
                          onClick={async () => {
                            try {
                              const response = await axios.get(`${API_URL}/api/integrations/test-stripe`);
                              if (response.data.success) {
                                toast.success(`✅ Stripe funciona correctamente. Cuenta: ${response.data.account_name || 'Verificada'}`);
                              } else {
                                toast.error(`❌ Error: ${response.data.error}`);
                              }
                            } catch (err) {
                              toast.error(`❌ Error al verificar Stripe: ${err.response?.data?.detail || err.message}`);
                            }
                          }}
                          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg transition-colors"
                          data-testid="test-stripe-btn"
                        >
                          <RefreshCw className="w-4 h-4" />
                          Comprobar conexión Stripe
                        </button>
                      </div>
                    ) : (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mt-2">
                        <p className="text-amber-400">Añade STRIPE_API_KEY en las variables de entorno para activar pagos.</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Brevo */}
                <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${integrations.brevo?.status === 'connected' ? 'bg-blue-600' : integrations.brevo?.configured ? 'bg-amber-600' : 'bg-gray-700'}`}>
                        <Mail className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Brevo</h3>
                        <p className="text-sm text-gray-400">Servicio de emails transaccionales</p>
                      </div>
                    </div>
                    {integrations.brevo?.status === 'connected' ? (
                      <span className="flex items-center gap-1 px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm">
                        <Wifi className="w-4 h-4" /> Conectado
                      </span>
                    ) : integrations.brevo?.configured ? (
                      <span className="flex items-center gap-1 px-3 py-1 bg-amber-500/20 text-amber-400 rounded-full text-sm">
                        <AlertCircle className="w-4 h-4" /> Error
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm">
                        <WifiOff className="w-4 h-4" /> No configurado
                      </span>
                    )}
                  </div>
                  
                  {/* Brevo Configuration Form */}
                  <div className="space-y-4 mt-4">
                    {integrations.brevo?.status === 'connected' ? (
                      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                        <p className="text-emerald-400 font-medium">✓ Conexión verificada</p>
                        {integrations.brevo?.email && <p className="text-gray-400 text-sm mt-1">Cuenta: {integrations.brevo.email}</p>}
                        {integrations.brevo?.plan && <p className="text-gray-400 text-sm">Plan: {integrations.brevo.plan}</p>}
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 gap-4">
                          <div>
                            <label className="block text-sm text-gray-400 mb-1">API Key de Brevo *</label>
                            <input 
                              type="password" 
                              value={settings.brevoApiKey || ''} 
                              onChange={(e) => setSettings({...settings, brevoApiKey: e.target.value})} 
                              placeholder="xkeysib-xxxxxxxx..." 
                              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-blue-500 focus:outline-none" 
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Obtén tu API key en <a href="https://app.brevo.com/settings/keys/api" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Brevo Dashboard → Settings → API Keys</a>
                            </p>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm text-gray-400 mb-1">Email Remitente</label>
                              <input 
                                type="email" 
                                value={settings.senderEmail || ''} 
                                onChange={(e) => setSettings({...settings, senderEmail: e.target.value})} 
                                placeholder="noreply@lokalgrow.com" 
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" 
                              />
                            </div>
                            <div>
                              <label className="block text-sm text-gray-400 mb-1">Nombre Remitente</label>
                              <input 
                                type="text" 
                                value={settings.senderName || ''} 
                                onChange={(e) => setSettings({...settings, senderName: e.target.value})} 
                                placeholder="Lokalgrow" 
                                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" 
                              />
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            if (!settings.brevoApiKey) {
                              toast.error('Introduce la API Key de Brevo');
                              return;
                            }
                            try {
                              await axios.post(`${API_URL}/api/admin/api-keys`, {
                                brevo_api_key: settings.brevoApiKey,
                                sender_email: settings.senderEmail,
                                sender_name: settings.senderName
                              });
                              toast.success('Configuración de Brevo guardada. Recargando...');
                              setTimeout(() => window.location.reload(), 1500);
                            } catch (err) {
                              toast.error('Error guardando configuración');
                            }
                          }}
                          className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                        >
                          <Mail className="w-4 h-4" />
                          Guardar y Conectar Brevo
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Brevo Lists Configuration */}
                <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-cyan-600">
                        <Users className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Listas de Brevo</h3>
                        <p className="text-sm text-gray-400">Sincroniza usuarios y negocios automáticamente</p>
                      </div>
                    </div>
                    {integrations.brevo?.configured && (
                      <span className="flex items-center gap-1 px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm">
                        <CheckCircle className="w-4 h-4" /> Brevo conectado
                      </span>
                    )}
                  </div>
                  
                  {!integrations.brevo?.configured ? (
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                      <p className="text-yellow-400 font-medium">⚠️ Configura Brevo primero</p>
                      <p className="text-gray-400 text-sm mt-1">Necesitas conectar tu cuenta de Brevo arriba para usar las listas.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-gray-400 text-sm">
                        Cuando un usuario o negocio se registre, será añadido automáticamente a la lista que configures.
                        Los contactos incluyen: nombre, email, teléfono, código postal y fecha de registro.
                      </p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Customer List */}
                        <div className="bg-gray-700/50 rounded-lg p-4">
                          <label className="block text-sm text-gray-300 mb-2 font-medium">
                            📱 Lista para Clientes
                          </label>
                          <input 
                            type="text" 
                            value={settings.brevoListCustomer || ''} 
                            onChange={(e) => setSettings({...settings, brevoListCustomer: e.target.value})} 
                            placeholder="ID de lista (ej: 3)" 
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white" 
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            ID numérico de la lista en Brevo
                          </p>
                        </div>
                        
                        {/* Business List */}
                        <div className="bg-gray-700/50 rounded-lg p-4">
                          <label className="block text-sm text-gray-300 mb-2 font-medium">
                            🏪 Lista para Negocios
                          </label>
                          <input 
                            type="text" 
                            value={settings.brevoListBusiness || ''} 
                            onChange={(e) => setSettings({...settings, brevoListBusiness: e.target.value})} 
                            placeholder="ID de lista (ej: 4)" 
                            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white" 
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            ID numérico de la lista en Brevo
                          </p>
                        </div>
                      </div>
                      
                      <button
                        onClick={async () => {
                          if (!settings.brevoListCustomer && !settings.brevoListBusiness) {
                            toast.error('Introduce al menos un ID de lista');
                            return;
                          }
                          try {
                            const payload = {};
                            if (settings.brevoListCustomer) {
                              payload.brevo_list_customer = settings.brevoListCustomer.trim();
                            }
                            if (settings.brevoListBusiness) {
                              payload.brevo_list_business = settings.brevoListBusiness.trim();
                            }
                            
                            await axios.post(`${API_URL}/api/admin/api-keys`, payload);
                            toast.success('Configuración de listas guardada');
                            setTimeout(() => window.location.reload(), 1500);
                          } catch (err) {
                            toast.error('Error guardando configuración');
                          }
                        }}
                        className="w-full px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                      >
                        <Users className="w-4 h-4" />
                        Guardar Configuración de Listas
                      </button>
                      
                      {/* Help info */}
                      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mt-2">
                        <p className="text-blue-400 text-sm font-medium">💡 ¿Cómo encontrar el ID de lista?</p>
                        <ol className="text-gray-400 text-xs mt-2 space-y-1 list-decimal list-inside">
                          <li>Ve a <a href="https://app.brevo.com/contact/list" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">Brevo → Contactos → Listas</a></li>
                          <li>Crea o selecciona una lista</li>
                          <li>El ID aparece en la URL: /list/<strong>123</strong></li>
                        </ol>
                      </div>
                    </div>
                  )}
                </div>

                {/* Twilio */}
                <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${integrations.twilio?.configured ? 'bg-green-600' : 'bg-gray-700'}`}>
                        <Phone className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Twilio</h3>
                        <p className="text-sm text-gray-400">Verificación SMS de teléfonos</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {integrations.twilio?.configured && (
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          integrations.twilio?.mode === 'live' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-amber-500/20 text-amber-400'
                        }`}>
                          {integrations.twilio?.mode === 'live' ? '🟢 LIVE' : '🟡 TEST'}
                        </span>
                      )}
                      {integrations.twilio?.configured ? (
                        <span className="flex items-center gap-1 px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm">
                          <Wifi className="w-4 h-4" /> Conectado
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm">
                          <WifiOff className="w-4 h-4" /> No configurado
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Twilio Configuration Form */}
                  <div className="space-y-4 mt-4">
                    {/* Configuration Form */}
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Account SID *</label>
                        <input 
                          type="text" 
                          id="twilio_account_sid_input"
                          value={settings.twilioAccountSid || ''} 
                          onChange={(e) => setSettings({...settings, twilioAccountSid: e.target.value})} 
                          placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" 
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white font-mono text-sm" 
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Auth Token *</label>
                        <input 
                          type="password" 
                          id="twilio_auth_token_input"
                          value={settings.twilioAuthToken || ''} 
                          onChange={(e) => setSettings({...settings, twilioAuthToken: e.target.value})} 
                          placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" 
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Verify Service SID *</label>
                        <input 
                          type="text" 
                          id="twilio_verify_service_input"
                          value={settings.twilioVerifyService || ''} 
                          onChange={(e) => setSettings({...settings, twilioVerifyService: e.target.value})} 
                          placeholder="VAxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" 
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white font-mono text-sm" 
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Número de Teléfono Twilio (opcional)</label>
                        <input 
                          type="text" 
                          id="twilio_phone_input"
                          value={settings.twilioPhoneNumber || ''} 
                          onChange={(e) => setSettings({...settings, twilioPhoneNumber: e.target.value})} 
                          placeholder="+34612345678" 
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white font-mono text-sm" 
                        />
                      </div>
                      
                      {/* Mode Selector */}
                      <div className="bg-gray-700/50 rounded-lg p-4">
                        <label className="block text-sm text-gray-300 mb-2 font-medium">Modo de operación *</label>
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => setSettings({...settings, twilioMode: 'test'})}
                            className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                              settings.twilioMode === 'test' 
                                ? 'border-amber-500 bg-amber-500/20 text-amber-400' 
                                : 'border-gray-600 bg-gray-800 text-gray-400 hover:border-gray-500'
                            }`}
                          >
                            <div className="text-center">
                              <span className="text-lg">🧪</span>
                              <p className="font-medium mt-1">TEST</p>
                              <p className="text-xs mt-1 opacity-75">Código fijo: 123456</p>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setSettings({...settings, twilioMode: 'live'})}
                            className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all ${
                              settings.twilioMode === 'live' 
                                ? 'border-green-500 bg-green-500/20 text-green-400' 
                                : 'border-gray-600 bg-gray-800 text-gray-400 hover:border-gray-500'
                            }`}
                          >
                            <div className="text-center">
                              <span className="text-lg">📱</span>
                              <p className="font-medium mt-1">LIVE</p>
                              <p className="text-xs mt-1 opacity-75">SMS reales</p>
                            </div>
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Encuentra estos valores en <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-green-400 hover:underline">Twilio Console</a>
                        </p>
                      </div>
                    </div>
                    
                    <button
                      onClick={async () => {
                        if (!settings.twilioAccountSid || !settings.twilioAuthToken || !settings.twilioVerifyService) {
                          toast.error('Completa los campos obligatorios de Twilio');
                          return;
                        }
                        if (!settings.twilioMode) {
                          toast.error('Selecciona un modo de operación (TEST o LIVE)');
                          return;
                        }
                        try {
                          const payload = {
                            twilio_account_sid: settings.twilioAccountSid.trim(),
                            twilio_auth_token: settings.twilioAuthToken.trim(),
                            twilio_verify_service: settings.twilioVerifyService.trim(),
                            twilio_phone_number: settings.twilioPhoneNumber?.trim() || '',
                            twilio_mode: settings.twilioMode
                          };
                          
                          console.log('Saving Twilio config:', { ...payload, twilio_auth_token: '***' });
                          
                          const response = await axios.post(`${API_URL}/api/admin/api-keys`, payload);
                          console.log('Twilio save response:', response.data);
                          
                          toast.success(`Twilio configurado en modo ${settings.twilioMode.toUpperCase()}. Guardadas ${response.data.saved?.length || 0} claves.`);
                          setTimeout(() => window.location.reload(), 1500);
                        } catch (err) {
                          console.error('Error saving Twilio:', err);
                          toast.error('Error: ' + (err.response?.data?.detail || err.message));
                        }
                      }}
                      disabled={!settings.twilioMode}
                      className={`w-full px-4 py-3 text-white rounded-lg transition-colors flex items-center justify-center gap-2 ${
                        settings.twilioMode 
                          ? 'bg-green-600 hover:bg-green-700' 
                          : 'bg-gray-600 cursor-not-allowed'
                      }`}
                    >
                      <Phone className="w-4 h-4" />
                      {integrations.twilio?.configured ? 'Actualizar Configuración' : 'Guardar y Conectar Twilio'}
                    </button>
                    
                    {/* Current config info */}
                    {integrations.twilio?.configured && (
                      <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 mt-2">
                        <p className="text-emerald-400 text-sm font-medium">✓ Twilio configurado</p>
                        <p className="text-gray-400 text-xs mt-1">
                          Modo actual: <strong className={integrations.twilio?.mode === 'live' ? 'text-green-400' : 'text-amber-400'}>
                            {integrations.twilio?.mode?.toUpperCase() || 'No definido'}
                          </strong>
                          {integrations.twilio?.phone_number && (
                            <> · Número: {integrations.twilio.phone_number}</>
                          )}
                        </p>
                        <p className="text-gray-500 text-xs mt-1">
                          Para cambiar de modo, rellena los campos y selecciona el nuevo modo.
                        </p>
                        
                        {/* Reload button */}
                        <button
                          onClick={async () => {
                            try {
                              const response = await axios.post(`${API_URL}/api/admin/integrations/twilio/reload`, {}, {
                                headers: { 'X-Admin-Id': admin?.id }
                              });
                              if (response.data.success) {
                                toast.success(`Twilio recargado: ${response.data.enabled ? 'Activo' : 'Inactivo'} - ${response.data.phone_number || 'Sin número'}`);
                              } else {
                                toast.error(response.data.message || 'Error al recargar');
                              }
                            } catch (err) {
                              toast.error('Error: ' + (err.response?.data?.detail || err.message));
                            }
                          }}
                          className="mt-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-colors"
                        >
                          🔄 Forzar Recarga de Credenciales
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Claude AI */}
                <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${integrations.claude_ai?.configured ? 'bg-violet-600' : 'bg-gray-700'}`}>
                        <Bot className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">Claude AI</h3>
                        <p className="text-sm text-gray-400">Marketing con IA</p>
                      </div>
                    </div>
                    {integrations.claude_ai?.configured ? (
                      <span className="flex items-center gap-1 px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm">
                        <Wifi className="w-4 h-4" /> Conectado
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm">
                        <WifiOff className="w-4 h-4" /> No configurado
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-400">
                    {integrations.claude_ai?.configured ? (
                      <p className="text-emerald-400">Claude Sonnet está listo. Las herramientas de marketing con IA están activas.</p>
                    ) : (
                      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mt-2">
                        <p className="text-amber-400">Añade EMERGENT_LLM_KEY en las variables de entorno.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <Cable className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No se pudo cargar el estado de las integraciones</p>
                <button 
                  onClick={fetchIntegrations}
                  className="mt-4 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                >
                  Reintentar
                </button>
              </div>
            )}

            {/* Instructions - Using Accordions */}
            <div className="mt-8 space-y-4">
              {/* Accordion: Configurar Stripe API */}
              <details className="group bg-indigo-500/10 border border-indigo-500/30 rounded-xl overflow-hidden">
                <summary className="flex items-center gap-4 p-5 cursor-pointer hover:bg-indigo-500/5 transition-colors">
                  <div className="w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <CreditCard className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-indigo-400 font-bold">Configurar Stripe API Key</h3>
                    <p className="text-gray-400 text-sm">Cómo conectar tu cuenta de Stripe para suscripciones B2B</p>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-5 pb-5 pt-0 border-t border-indigo-500/20">
                  <div className="space-y-3 mt-4">
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 bg-indigo-600/50 rounded-full flex items-center justify-center text-xs text-white flex-shrink-0 mt-0.5">1</span>
                      <div>
                        <p className="text-white text-sm">Accede a tu Dashboard de Stripe</p>
                        <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-indigo-400 text-sm underline hover:text-indigo-300 inline-flex items-center gap-1">
                          dashboard.stripe.com/apikeys
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 bg-indigo-600/50 rounded-full flex items-center justify-center text-xs text-white flex-shrink-0 mt-0.5">2</span>
                      <div>
                        <p className="text-white text-sm">Copia tu "Secret Key"</p>
                        <p className="text-gray-400 text-xs">Empieza con <code className="bg-gray-700 px-1 rounded">sk_live_</code> (producción) o <code className="bg-gray-700 px-1 rounded">sk_test_</code> (pruebas)</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <span className="w-6 h-6 bg-indigo-600/50 rounded-full flex items-center justify-center text-xs text-white flex-shrink-0 mt-0.5">3</span>
                      <div>
                        <p className="text-white text-sm">Añade la variable en tu servidor</p>
                        <div className="bg-gray-800 rounded-lg p-3 mt-2 font-mono text-sm">
                          <span className="text-gray-500"># En tu archivo .env del backend</span>
                          <br />
                          <span className="text-indigo-400">STRIPE_API_KEY</span>=<span className="text-emerald-400">sk_live_tu_clave_aqui</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Webhook info */}
                  <div className="mt-4 bg-gray-800/50 rounded-lg p-4">
                    <h4 className="text-white text-sm font-semibold mb-2">Webhook para suscripciones (Recomendado)</h4>
                    <p className="text-gray-400 text-xs mb-2">Configura este webhook en Stripe para recibir actualizaciones de suscripciones:</p>
                    <div className="bg-gray-900 rounded p-2 font-mono text-xs break-all">
                      <span className="text-gray-500">URL: </span>
                      <span className="text-emerald-400">{API_URL}/api/subscriptions/webhook</span>
                    </div>
                    <p className="text-gray-500 text-xs mt-2">Eventos recomendados: <code className="bg-gray-700 px-1 rounded">checkout.session.completed</code>, <code className="bg-gray-700 px-1 rounded">customer.subscription.updated</code></p>
                  </div>
                </div>
              </details>

              {/* Accordion: Variables de Entorno */}
              <details className="group bg-blue-500/10 border border-blue-500/30 rounded-xl overflow-hidden">
                <summary className="flex items-center gap-4 p-5 cursor-pointer hover:bg-blue-500/5 transition-colors">
                  <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <AlertCircle className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-blue-400 font-bold">Variables de Entorno</h3>
                    <p className="text-gray-400 text-sm">Resumen de todas las variables necesarias</p>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-5 pb-5 pt-0 border-t border-blue-500/20">
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-800/50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-indigo-400 text-xs">Stripe (Suscripciones B2B)</p>
                        {integrations?.stripe?.configured ? (
                          <span className="text-emerald-400 text-xs flex items-center gap-1"><Wifi className="w-3 h-3" /> OK</span>
                        ) : (
                          <span className="text-red-400 text-xs flex items-center gap-1"><WifiOff className="w-3 h-3" /> Falta</span>
                        )}
                      </div>
                      <p className="text-gray-300 font-mono text-xs">STRIPE_API_KEY=sk_live_...</p>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-blue-400 text-xs">Brevo (Emails)</p>
                        {integrations?.brevo?.status === 'connected' ? (
                          <span className="text-emerald-400 text-xs flex items-center gap-1"><Wifi className="w-3 h-3" /> OK</span>
                        ) : (
                          <span className="text-red-400 text-xs flex items-center gap-1"><WifiOff className="w-3 h-3" /> Falta</span>
                        )}
                      </div>
                      <p className="text-gray-300 font-mono text-xs">BREVO_API_KEY=xkeysib-...</p>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-green-400 text-xs">Twilio (SMS)</p>
                        {integrations?.twilio?.configured ? (
                          <span className="text-emerald-400 text-xs flex items-center gap-1"><Wifi className="w-3 h-3" /> OK</span>
                        ) : (
                          <span className="text-red-400 text-xs flex items-center gap-1"><WifiOff className="w-3 h-3" /> Falta</span>
                        )}
                      </div>
                      <p className="text-gray-300 font-mono text-xs">TWILIO_ACCOUNT_SID=AC...</p>
                      <p className="text-gray-300 font-mono text-xs">TWILIO_AUTH_TOKEN=...</p>
                      <p className="text-gray-300 font-mono text-xs">TWILIO_PHONE_NUMBER=+34...</p>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-violet-400 text-xs">Claude AI (Marketing + OCR)</p>
                        {integrations?.claude_ai?.configured ? (
                          <span className="text-emerald-400 text-xs flex items-center gap-1"><Wifi className="w-3 h-3" /> OK</span>
                        ) : (
                          <span className="text-red-400 text-xs flex items-center gap-1"><WifiOff className="w-3 h-3" /> Falta</span>
                        )}
                      </div>
                      <p className="text-gray-300 font-mono text-xs">EMERGENT_LLM_KEY=sk-...</p>
                    </div>
                  </div>
                </div>
              </details>

              {/* Accordion: API Keys Configuration */}
              <details className="group bg-amber-500/10 border border-amber-500/30 rounded-xl overflow-hidden" open>
                <summary className="flex items-center gap-4 p-5 cursor-pointer hover:bg-amber-500/5 transition-colors">
                  <div className="w-10 h-10 bg-amber-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <KeyRound className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-amber-400 font-bold">Configurar API Keys desde el Panel</h3>
                    <p className="text-gray-400 text-sm">Pega tus claves API aquí si no tienes acceso al servidor</p>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-5 pb-5 pt-0 border-t border-amber-500/20">
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Stripe API Key */}
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <label className="block text-indigo-400 text-sm font-medium mb-2">
                        Stripe API Key (Secret)
                      </label>
                      <input
                        type="password"
                        placeholder="sk_live_..."
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:border-indigo-500 focus:outline-none"
                        id="stripe_api_key_input"
                      />
                      <p className="text-gray-500 text-xs mt-1">Para suscripciones B2B</p>
                    </div>

                    {/* Stripe Webhook Secret */}
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <label className="block text-indigo-400 text-sm font-medium mb-2">
                        Stripe Webhook Secret
                      </label>
                      <input
                        type="password"
                        placeholder="whsec_..."
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:border-indigo-500 focus:outline-none"
                        id="stripe_webhook_secret_input"
                      />
                      <p className="text-gray-500 text-xs mt-1">Para verificar webhooks</p>
                    </div>

                    {/* Brevo API Key */}
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <label className="block text-blue-400 text-sm font-medium mb-2">
                        Brevo API Key
                      </label>
                      <input
                        type="password"
                        placeholder="xkeysib-..."
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:border-blue-500 focus:outline-none"
                        id="brevo_api_key_input"
                      />
                      <p className="text-gray-500 text-xs mt-1">Para emails transaccionales</p>
                    </div>

                    {/* Twilio SID */}
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <label className="block text-green-400 text-sm font-medium mb-2">
                        Twilio Account SID
                      </label>
                      <input
                        type="text"
                        placeholder="AC..."
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:border-green-500 focus:outline-none"
                        id="twilio_account_sid_input"
                      />
                    </div>

                    {/* Twilio Auth Token */}
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <label className="block text-green-400 text-sm font-medium mb-2">
                        Twilio Auth Token
                      </label>
                      <input
                        type="password"
                        placeholder="Token de autenticación"
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:border-green-500 focus:outline-none"
                        id="twilio_auth_token_input"
                      />
                    </div>

                    {/* Twilio Phone */}
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <label className="block text-green-400 text-sm font-medium mb-2">
                        Twilio Phone Number (Default)
                      </label>
                      <input
                        type="text"
                        placeholder="+1..."
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:border-green-500 focus:outline-none"
                        id="twilio_phone_input"
                      />
                      <p className="text-gray-500 text-xs mt-1">Número principal para enviar SMS (EE.UU.)</p>
                    </div>
                  </div>
                  
                  {/* Local Numbers Section */}
                  <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 bg-amber-600 rounded-lg flex items-center justify-center">
                        <Globe className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h4 className="text-amber-400 font-bold text-sm">Números Locales por País</h4>
                        <p className="text-gray-400 text-xs">Sara usará el número local del país del destinatario para mejor entrega</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-amber-300 text-sm font-medium mb-1 flex items-center gap-2">
                          <span>🇪🇸</span> España (+34)
                        </label>
                        <input
                          type="text"
                          placeholder="+34612345678"
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none"
                          id="local_number_es_input"
                        />
                      </div>
                      <div>
                        <label className="block text-amber-300 text-sm font-medium mb-1 flex items-center gap-2">
                          <span>🇫🇷</span> Francia (+33)
                        </label>
                        <input
                          type="text"
                          placeholder="+33612345678"
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none"
                          id="local_number_fr_input"
                        />
                      </div>
                      <div>
                        <label className="block text-amber-300 text-sm font-medium mb-1 flex items-center gap-2">
                          <span>🇬🇧</span> Reino Unido (+44)
                        </label>
                        <input
                          type="text"
                          placeholder="+447123456789"
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none"
                          id="local_number_uk_input"
                        />
                      </div>
                      <div>
                        <label className="block text-amber-300 text-sm font-medium mb-1 flex items-center gap-2">
                          <span>🇺🇸</span> Estados Unidos (+1)
                        </label>
                        <input
                          type="text"
                          placeholder="+15705651588"
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:border-amber-500 focus:outline-none"
                          id="local_number_us_input"
                        />
                      </div>
                    </div>
                    
                    <div className="mt-4 p-3 bg-gray-800/50 rounded-lg">
                      <p className="text-amber-300 text-xs flex items-center gap-2">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        Configura el webhook en cada número de Twilio:
                      </p>
                      <code className="block mt-2 text-xs bg-gray-900 p-2 rounded text-green-400 font-mono break-all">
                        https://lokalgrow.com/api/reactivation/webhook/sms
                      </code>
                    </div>
                  </div>

                  <button
                    onClick={async () => {
                      const keys = {
                        stripe_api_key: document.getElementById('stripe_api_key_input')?.value,
                        stripe_webhook_secret: document.getElementById('stripe_webhook_secret_input')?.value,
                        brevo_api_key: document.getElementById('brevo_api_key_input')?.value,
                        twilio_account_sid: document.getElementById('twilio_account_sid_input')?.value,
                        twilio_auth_token: document.getElementById('twilio_auth_token_input')?.value,
                        twilio_phone_number: document.getElementById('twilio_phone_input')?.value
                      };
                      
                      // Local numbers by country
                      const localNumbers = {
                        ES: document.getElementById('local_number_es_input')?.value?.trim() || null,
                        FR: document.getElementById('local_number_fr_input')?.value?.trim() || null,
                        UK: document.getElementById('local_number_uk_input')?.value?.trim() || null,
                        US: document.getElementById('local_number_us_input')?.value?.trim() || null
                      };
                      
                      const nonEmpty = Object.fromEntries(
                        Object.entries(keys).filter(([_, v]) => v && v.trim())
                      );
                      
                      const hasLocalNumbers = Object.values(localNumbers).some(v => v);
                      
                      if (Object.keys(nonEmpty).length === 0 && !hasLocalNumbers) {
                        toast.error('Introduce al menos una API key o número local');
                        return;
                      }
                      
                      try {
                        // Save API keys
                        if (Object.keys(nonEmpty).length > 0) {
                          const res = await axios.post(`${API_URL}/api/admin/api-keys`, nonEmpty);
                          toast.success(`${res.data.saved.length} clave(s) guardadas correctamente`);
                        }
                        
                        // Save local numbers if any
                        if (hasLocalNumbers) {
                          const cleanLocalNumbers = Object.fromEntries(
                            Object.entries(localNumbers).filter(([_, v]) => v)
                          );
                          await axios.post(`${API_URL}/api/admin/integrations/twilio/local-numbers`, cleanLocalNumbers, {
                            headers: { 'X-Admin-Id': admin?.id }
                          });
                          toast.success('Números locales guardados correctamente');
                        }
                        
                        // Clear inputs
                        Object.keys(keys).forEach(k => {
                          const el = document.getElementById(k + '_input');
                          if (el) el.value = '';
                        });
                        ['es', 'fr', 'uk', 'us'].forEach(c => {
                          const el = document.getElementById(`local_number_${c}_input`);
                          if (el) el.value = '';
                        });
                        
                        fetchIntegrations();
                      } catch (err) {
                        toast.error('Error al guardar las configuraciones');
                      }
                    }}
                    className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors font-medium"
                    data-testid="save-api-keys-btn"
                  >
                    <Save className="w-5 h-5" />
                    Guardar API Keys y Números Locales
                  </button>
                </div>
              </details>

              {/* Accordion: Branding */}
              <details className="group bg-violet-500/10 border border-violet-500/30 rounded-xl overflow-hidden" open>
                <summary className="flex items-center gap-4 p-5 cursor-pointer hover:bg-violet-500/5 transition-colors">
                  <div className="w-10 h-10 bg-violet-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Download className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-violet-400 font-bold">Branding y Recursos</h3>
                    <p className="text-gray-400 text-sm">Logos oficiales para Stripe, redes sociales y más</p>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 transition-transform group-open:rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-5 pb-5 pt-0 border-t border-violet-500/20">
                  <div className="mt-4">
                    <p className="text-gray-300 text-sm font-medium mb-4">Logos Oficiales para Descargar</p>
                    
                    {/* Logo Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      {/* Logo Horizontal */}
                      <div className="bg-gray-800/50 rounded-lg p-4">
                        <div className="w-full h-24 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 rounded-lg flex items-center justify-center p-4 mb-3">
                          <img src={LOGO_TRANSPARENT} alt="Logo Horizontal" className="h-full w-auto object-contain" />
                        </div>
                        <div className="space-y-2">
                          <h4 className="text-white font-medium text-sm">Logo Horizontal (Transparente)</h4>
                          <p className="text-gray-400 text-xs">Web, documentos, firmas de email</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="bg-gray-700 px-2 py-0.5 rounded">PNG Transparente</span>
                            <span className="bg-gray-700 px-2 py-0.5 rounded">Horizontal</span>
                          </div>
                          <a 
                            href="/lokalgrow-logo-transparent.png" 
                            download="lokalgrow-logo-horizontal.png"
                            className="flex items-center justify-center gap-2 w-full px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm rounded-lg transition-colors mt-2"
                            data-testid="download-logo-btn"
                          >
                            <Download className="w-4 h-4" />
                            Descargar
                          </a>
                        </div>
                      </div>

                      {/* Favicon/Icon */}
                      <div className="bg-gray-800/50 rounded-lg p-4">
                        <div className="w-full h-24 bg-gray-900 rounded-lg flex items-center justify-center p-4 mb-3">
                          <img src={FAVICON} alt="Favicon" className="h-16 w-16 object-contain" />
                        </div>
                        <div className="space-y-2">
                          <h4 className="text-white font-medium text-sm">Icono / Favicon</h4>
                          <p className="text-gray-400 text-xs">Stripe branding, favicon, apps</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="bg-gray-700 px-2 py-0.5 rounded">64×64 px</span>
                            <span className="bg-gray-700 px-2 py-0.5 rounded">Cuadrado</span>
                          </div>
                          <a 
                            href="/lokalgrow-icon.png" 
                            download="lokalgrow-favicon.png"
                            className="flex items-center justify-center gap-2 w-full px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm rounded-lg transition-colors mt-2"
                            data-testid="download-favicon-btn"
                          >
                            <Download className="w-4 h-4" />
                            Descargar
                          </a>
                        </div>
                      </div>

                      {/* Pin for Maps */}
                      <div className="bg-gray-800/50 rounded-lg p-4">
                        <div className="w-full h-24 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center gap-4 p-4 mb-3">
                          <img src={PIN_ICON_16} alt="Pin PNG" className="h-8 w-8 object-contain" />
                          <img src={PIN_ICON_SVG} alt="Pin SVG" className="h-8 w-8 object-contain" />
                        </div>
                        <div className="space-y-2">
                          <h4 className="text-white font-medium text-sm">Pin para Mapas</h4>
                          <p className="text-gray-400 text-xs">Marcadores de ubicación en mapas</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="bg-gray-700 px-2 py-0.5 rounded">16×16 px</span>
                            <span className="bg-gray-700 px-2 py-0.5 rounded">PNG + SVG</span>
                          </div>
                          <div className="flex gap-2 mt-2">
                            <a 
                              href="/lokalgrow-pin-16.png" 
                              download="lokalgrow-pin.png"
                              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white text-xs rounded-lg transition-colors"
                            >
                              <Download className="w-3 h-3" />
                              PNG
                            </a>
                            <a 
                              href="/lokalgrow-pin.svg" 
                              download="lokalgrow-pin.svg"
                              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white text-xs rounded-lg transition-colors"
                            >
                              <Download className="w-3 h-3" />
                              SVG
                            </a>
                          </div>
                        </div>
                      </div>

                      {/* Redes Sociales */}
                      <div className="bg-gray-800/50 rounded-lg p-4">
                        <div className="w-full h-24 bg-emerald-600 rounded-lg flex items-center justify-center p-4 mb-3">
                          <img src={FAVICON} alt="Redes Sociales" className="h-16 w-16 object-contain" />
                        </div>
                        <div className="space-y-2">
                          <h4 className="text-white font-medium text-sm">Redes Sociales</h4>
                          <p className="text-gray-400 text-xs">Perfil de Instagram, Facebook, Twitter</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <span className="bg-gray-700 px-2 py-0.5 rounded">64×64 px</span>
                            <span className="bg-gray-700 px-2 py-0.5 rounded">Cuadrado</span>
                          </div>
                          <a 
                            href="/lokalgrow-icon.png" 
                            download="lokalgrow-social.png"
                            className="flex items-center justify-center gap-2 w-full px-3 py-2 bg-violet-600 hover:bg-violet-700 text-white text-sm rounded-lg transition-colors mt-2"
                            data-testid="download-social-btn"
                          >
                            <Download className="w-4 h-4" />
                            Descargar
                          </a>
                        </div>
                      </div>
                    </div>

                    {/* Brand Colors */}
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <p className="text-gray-300 text-sm font-medium mb-3">Colores de Marca</p>
                      <div className="grid grid-cols-3 gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg" style={{backgroundColor: '#0D3320'}}></div>
                          <div>
                            <p className="text-white text-sm font-mono">#0D3320</p>
                            <p className="text-gray-500 text-xs">Verde Oscuro</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg" style={{backgroundColor: '#10B981'}}></div>
                          <div>
                            <p className="text-white text-sm font-mono">#10B981</p>
                            <p className="text-gray-500 text-xs">Verde Principal</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg border border-gray-600" style={{backgroundColor: '#FFFFFF'}}></div>
                          <div>
                            <p className="text-white text-sm font-mono">#FFFFFF</p>
                            <p className="text-gray-500 text-xs">Blanco</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </details>
            </div>
            
            {/* New Integrations Configuration Panel */}
            <div className="mt-8 pt-8 border-t border-gray-700">
              <IntegrationsConfigPanel adminId={admin?.id} />
            </div>
          </motion.div>
        )}

        {/* Ads Tab */}
        {activeTab === 'ads' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Gestión de Publicidad</h2>
              <button onClick={() => setShowAdForm(true)} className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors">
                <Plus className="w-5 h-5" /> Nuevo Anuncio
              </button>
            </div>

            {/* Ad Specifications Info */}
            <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-5 mb-6">
              <h3 className="text-amber-400 font-bold text-lg mb-4 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Especificaciones de Imágenes por Zona
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Header Carousel */}
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    </div>
                    <h4 className="text-white font-medium">Header Carousel</h4>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Tamaño:</span>
                      <span className="text-white font-mono">1920 × 600 px</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Orientación:</span>
                      <span className="text-emerald-400">Horizontal (Panorámico)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Ratio:</span>
                      <span className="text-gray-300">16:5</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Formato:</span>
                      <span className="text-gray-300">JPG, PNG, WebP</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Max peso:</span>
                      <span className="text-gray-300">500 KB</span>
                    </div>
                  </div>
                  <div className="mt-3 p-2 bg-indigo-500/10 rounded text-xs text-indigo-300">
                    Visible en la parte superior de la landing page
                  </div>
                </div>

                {/* Sidebar Banner */}
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-violet-600 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" /></svg>
                    </div>
                    <h4 className="text-white font-medium">Banner Lateral</h4>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Tamaño:</span>
                      <span className="text-white font-mono">300 × 600 px</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Orientación:</span>
                      <span className="text-blue-400">Vertical (Half Page)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Ratio:</span>
                      <span className="text-gray-300">1:2</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Formato:</span>
                      <span className="text-gray-300">JPG, PNG, WebP</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Max peso:</span>
                      <span className="text-gray-300">200 KB</span>
                    </div>
                  </div>
                  <div className="mt-3 p-2 bg-violet-500/10 rounded text-xs text-violet-300">
                    Visible en el lateral derecho del listado
                  </div>
                </div>

                {/* Featured Listing */}
                <div className="bg-gray-800/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-amber-600 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
                    </div>
                    <h4 className="text-white font-medium">Destacado</h4>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Tamaño:</span>
                      <span className="text-white font-mono">400 × 300 px</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Orientación:</span>
                      <span className="text-emerald-400">Horizontal (Card)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Ratio:</span>
                      <span className="text-gray-300">4:3</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Formato:</span>
                      <span className="text-gray-300">JPG, PNG, WebP</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Max peso:</span>
                      <span className="text-gray-300">150 KB</span>
                    </div>
                  </div>
                  <div className="mt-3 p-2 bg-amber-500/10 rounded text-xs text-amber-300">
                    Card destacada entre los resultados
                  </div>
                </div>
              </div>
            </div>

            {showAdForm && (
              <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-gray-800 rounded-xl p-6 w-full max-w-lg border border-gray-700">
                  <h3 className="text-xl font-bold text-white mb-4">Nuevo Espacio Publicitario</h3>
                  <form onSubmit={handleCreateAd} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Posición</label>
                        <select value={adForm.position} onChange={(e) => setAdForm({...adForm, position: e.target.value, price: pricing[e.target.value]?.price || 0})} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white">
                          <option value="header_carousel">Header Carousel (€2,997)</option>
                          <option value="sidebar_banner">Banner Lateral (€997)</option>
                          <option value="featured_listing">Destacado (€1,497)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Slot</label>
                        <input type="number" min="1" max={pricing[adForm.position]?.slots || 3} value={adForm.slot} onChange={(e) => setAdForm({...adForm, slot: parseInt(e.target.value)})} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" />
                      </div>
                    </div>
                    <div><label className="block text-sm text-gray-400 mb-1">Nombre del Cliente</label><input type="text" required value={adForm.clientName} onChange={(e) => setAdForm({...adForm, clientName: e.target.value})} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" /></div>
                    <div><label className="block text-sm text-gray-400 mb-1">Email del Cliente</label><input type="email" required value={adForm.clientEmail} onChange={(e) => setAdForm({...adForm, clientEmail: e.target.value})} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" /></div>
                    <div><label className="block text-sm text-gray-400 mb-1">URL de la Imagen</label><input type="url" required value={adForm.imageUrl} onChange={(e) => setAdForm({...adForm, imageUrl: e.target.value})} placeholder="https://..." className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" /></div>
                    <div><label className="block text-sm text-gray-400 mb-1">URL de Destino</label><input type="url" required value={adForm.linkUrl} onChange={(e) => setAdForm({...adForm, linkUrl: e.target.value})} placeholder="https://..." className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><label className="block text-sm text-gray-400 mb-1">Fecha Inicio</label><input type="date" required value={adForm.startDate} onChange={(e) => setAdForm({...adForm, startDate: e.target.value})} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" /></div>
                      <div><label className="block text-sm text-gray-400 mb-1">Fecha Fin</label><input type="date" required value={adForm.endDate} onChange={(e) => setAdForm({...adForm, endDate: e.target.value})} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" /></div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <button type="button" onClick={() => setShowAdForm(false)} className="px-4 py-2 text-gray-400 hover:text-white transition-colors">Cancelar</button>
                      <button type="submit" className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors">Crear Anuncio</button>
                    </div>
                  </form>
                </motion.div>
              </div>
            )}

            <div className="space-y-4">
              {ads.length === 0 ? (
                <div className="bg-gray-800 rounded-xl p-8 text-center border border-gray-700">
                  <Megaphone className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No hay anuncios configurados</p>
                </div>
              ) : (
                ads.map(ad => (
                  <div key={ad.id} className="bg-gray-800 rounded-xl p-4 border border-gray-700 flex items-center gap-4">
                    <img src={ad.imageUrl} alt={ad.clientName} className="w-20 h-14 object-cover rounded-lg bg-gray-700" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="text-white font-medium">{ad.clientName}</h4>
                        <span className={`text-xs px-2 py-0.5 rounded ${ad.isActive ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-600 text-gray-400'}`}>{ad.isActive ? 'Activo' : 'Inactivo'}</span>
                      </div>
                      <p className="text-gray-400 text-sm">{pricing[ad.position]?.name} - Slot {ad.slot} - €{ad.price}/mes</p>
                      <p className="text-gray-500 text-xs">{new Date(ad.startDate).toLocaleDateString()} - {new Date(ad.endDate).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => toggleAd(ad.id)} className={`p-2 rounded-lg transition-colors ${ad.isActive ? 'text-amber-400 hover:bg-amber-500/10' : 'text-emerald-400 hover:bg-emerald-500/10'}`}>
                        {ad.isActive ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                      <button onClick={() => deleteAd(ad.id)} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"><Trash2 className="w-5 h-5" /></button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}

        {/* Support Tab */}
        {activeTab === 'support' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-2xl font-bold text-white mb-6">Mensajes de Contacto</h2>
            
            <div className="space-y-4">
              {supportMessages.length === 0 ? (
                <div className="bg-gray-800 rounded-xl p-8 text-center border border-gray-700">
                  <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400">No hay mensajes de contacto</p>
                </div>
              ) : (
                supportMessages.map(msg => (
                  <div key={msg.id || msg.email} className={`bg-gray-800 rounded-xl p-6 border ${msg.read ? 'border-gray-700' : 'border-emerald-500/50'}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-white font-medium">{msg.name}</h4>
                          {!msg.read && <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400">Nuevo</span>}
                          {msg.status === 'replied' && <span className="text-xs px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400">Respondido</span>}
                        </div>
                        <p className="text-gray-400 text-sm">{msg.email}</p>
                      </div>
                      <span className="text-gray-500 text-xs">{msg.created_at ? new Date(msg.created_at).toLocaleString() : 'N/A'}</span>
                    </div>
                    <p className="text-gray-300 mb-4 whitespace-pre-wrap">{msg.message}</p>
                    
                    {msg.reply ? (
                      <div className="bg-gray-700/50 rounded-lg p-4">
                        <p className="text-xs text-gray-400 mb-2">Tu respuesta ({msg.reply_at ? new Date(msg.reply_at).toLocaleString() : ''}):</p>
                        <p className="text-gray-300">{msg.reply}</p>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <input type="text" placeholder="Escribe tu respuesta..." className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white" id={`reply-${msg.id}`} />
                        <button onClick={async () => { 
                          const input = document.getElementById(`reply-${msg.id}`); 
                          if(input.value) {
                            try {
                              await axios.put(`${API_URL}/api/admin/contact-messages/${msg.id}/reply`, { reply: input.value }, { headers: getHeaders() });
                              fetchDashboard();
                              input.value = '';
                            } catch(e) { console.error(e); }
                          }
                        }} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors">Responder</button>
                        {!msg.read && <button onClick={async () => {
                          try {
                            await axios.put(`${API_URL}/api/admin/contact-messages/${msg.id}/read`, {}, { headers: getHeaders() });
                            fetchDashboard();
                          } catch(e) { console.error(e); }
                        }} className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg transition-colors">Marcar leído</button>}
                        <button onClick={async () => {
                          if(window.confirm('¿Eliminar este mensaje?')) {
                            try {
                              await axios.delete(`${API_URL}/api/admin/contact-messages/${msg.id}`, { headers: getHeaders() });
                              fetchDashboard();
                            } catch(e) { console.error(e); }
                          }
                        }} className="px-4 py-2 bg-red-600/50 hover:bg-red-600 text-white rounded-lg transition-colors">Eliminar</button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}

        {/* Legal Agreements Tab */}
        {activeTab === 'legal-agreements' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <AdminLegalAgreementsPanel />
          </motion.div>
        )}

        {/* Legal Tab */}
        {activeTab === 'legal' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Textos Legales GDPR</h2>
              <a 
                href="/privacy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-emerald-400 hover:text-emerald-300"
              >
                Ver en web →
              </a>
            </div>
            
            <div className="flex flex-wrap gap-2 mb-6">
              {[
                { id: 'privacy', label: 'Privacidad', icon: '🔒' },
                { id: 'terms', label: 'Términos', icon: '📋' },
                { id: 'cookies', label: 'Cookies', icon: '🍪' },
                { id: 'legal_notice', label: 'Aviso Legal', icon: '⚖️' }
              ].map(page => (
                <button
                  key={page.id}
                  onClick={() => setSelectedLegalPage(page.id)}
                  className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${selectedLegalPage === page.id ? 'bg-emerald-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
                >
                  <span>{page.icon}</span>
                  {page.label}
                </button>
              ))}
            </div>

            <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-2">Título de la página</label>
                <input
                  type="text"
                  value={legalPages[selectedLegalPage]?.title || ''}
                  onChange={(e) => setLegalPages({...legalPages, [selectedLegalPage]: {...legalPages[selectedLegalPage], title: e.target.value}})}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm text-gray-400 mb-2">Contenido (Markdown)</label>
                <textarea
                  value={legalPages[selectedLegalPage]?.content || ''}
                  onChange={(e) => setLegalPages({...legalPages, [selectedLegalPage]: {...legalPages[selectedLegalPage], content: e.target.value}})}
                  rows={20}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white font-mono text-sm"
                  placeholder="# Título

## Sección 1
Tu texto aquí...

## Sección 2
- Punto 1
- Punto 2"
                />
              </div>

              <div className="flex items-center justify-between">
                <p className="text-gray-500 text-sm">Formato Markdown: # títulos, ## subtítulos, **negrita**, - listas, | tablas |</p>
                <button
                  onClick={() => saveLegalPage(selectedLegalPage)}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-600/50 text-white rounded-lg transition-colors"
                >
                  <Save className="w-5 h-5" />
                  {saving ? 'Guardando...' : 'Guardar Página'}
                </button>
              </div>
            </div>
            
            {/* Info about GDPR defaults */}
            <div className="mt-6 bg-blue-900/20 border border-blue-800 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-blue-400 font-medium">Textos GDPR Pre-configurados</p>
                  <p className="text-blue-400/70 text-sm mt-1">
                    Los textos legales están pre-configurados con contenido compliant GDPR para España y la UE. 
                    Si no has modificado ninguno, se mostrarán los textos por defecto en español, inglés y francés.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Platform Settings Tab */}
        {activeTab === 'platform-settings' && (
          <PlatformSettingsPanel />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-2xl font-bold text-white mb-6">Configuración General</h2>
            
            <div className="space-y-6">
              {/* Tracking IDs */}
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                  IDs de Tracking y Analytics
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { key: 'googleAnalyticsId', label: 'Google Analytics ID', placeholder: 'G-XXXXXXXXXX' },
                    { key: 'facebookPixelId', label: 'Facebook Pixel ID', placeholder: 'XXXXXXXXXXXXXXX' },
                    { key: 'cookiebotId', label: 'Cookiebot ID', placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' },
                    { key: 'googleAdsId', label: 'Google Ads ID', placeholder: 'AW-XXXXXXXXX' },
                    { key: 'tiktokPixelId', label: 'TikTok Pixel ID', placeholder: 'XXXXXXXXXXXXXXXXXX' }
                  ].map(field => (
                    <div key={field.key}>
                      <label className="block text-sm text-gray-400 mb-1">{field.label}</label>
                      <input type="text" value={settings[field.key] || ''} onChange={(e) => setSettings({...settings, [field.key]: e.target.value})} placeholder={field.placeholder} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Toggle Settings */}
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-4">Opciones del Sitio</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div><p className="text-white font-medium">Activar Publicidad</p><p className="text-gray-400 text-sm">Mostrar anuncios en el sitio</p></div>
                    <button onClick={() => setSettings({...settings, adsEnabled: !settings.adsEnabled})} className={`w-14 h-8 rounded-full transition-colors ${settings.adsEnabled ? 'bg-emerald-600' : 'bg-gray-600'}`}>
                      <div className={`w-6 h-6 bg-white rounded-full transition-transform ${settings.adsEnabled ? 'translate-x-7' : 'translate-x-1'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <div><p className="text-white font-medium">Modo Mantenimiento</p><p className="text-gray-400 text-sm">Mostrar página de mantenimiento a usuarios</p></div>
                    <button onClick={() => setSettings({...settings, maintenanceMode: !settings.maintenanceMode})} className={`w-14 h-8 rounded-full transition-colors ${settings.maintenanceMode ? 'bg-amber-600' : 'bg-gray-600'}`}>
                      <div className={`w-6 h-6 bg-white rounded-full transition-transform ${settings.maintenanceMode ? 'translate-x-7' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <p className="text-blue-400 text-sm flex items-center gap-2">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span>Para configurar <strong>Brevo</strong> (emails), <strong>Twilio</strong> (SMS) y <strong>Stripe</strong> (pagos), ve a la pestaña <strong>Integraciones</strong>.</span>
                </p>
              </div>

              <button onClick={saveSettings} disabled={saving} className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-600/50 text-white font-semibold rounded-lg transition-colors">
                {saving ? 'Guardando...' : 'Guardar Configuración'}
              </button>
            </div>
          </motion.div>
        )}

        {/* Account Tab */}
        {activeTab === 'account' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <h2 className="text-2xl font-bold text-white mb-6">Mi Cuenta</h2>
            
            <div className="space-y-6 max-w-2xl">
              {/* Account Info */}
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-emerald-400" />
                  Información de la Cuenta
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">ID de Admin</label>
                    <div className="px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-lg text-gray-400 font-mono text-sm">
                      {admin?.id || 'N/A'}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Los <strong className="text-amber-400">últimos 8 caracteres</strong> son tu clave secreta para resetear contraseña: 
                      <span className="ml-1 font-mono text-amber-400">{admin?.id?.slice(-8) || '********'}</span>
                    </p>
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="email"
                        value={accountForm.email}
                        onChange={(e) => setAccountForm({...accountForm, email: e.target.value})}
                        className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="tu@email.com"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Change Password */}
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-emerald-400" />
                  Cambiar Contraseña
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Contraseña Actual</label>
                    <div className="relative">
                      <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                      <input
                        type="password"
                        value={accountForm.currentPassword}
                        onChange={(e) => setAccountForm({...accountForm, currentPassword: e.target.value})}
                        className="w-full pl-11 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Nueva Contraseña</label>
                      <input
                        type="password"
                        value={accountForm.newPassword}
                        onChange={(e) => setAccountForm({...accountForm, newPassword: e.target.value})}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="••••••••"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Confirmar Nueva Contraseña</label>
                      <input
                        type="password"
                        value={accountForm.confirmPassword}
                        onChange={(e) => setAccountForm({...accountForm, confirmPassword: e.target.value})}
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                  
                  {accountForm.newPassword && accountForm.confirmPassword && accountForm.newPassword !== accountForm.confirmPassword && (
                    <p className="text-red-400 text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Las contraseñas no coinciden
                    </p>
                  )}
                </div>
              </div>

              {/* Save Button */}
              <button
                onClick={saveAccountChanges}
                disabled={saving || (accountForm.newPassword && accountForm.newPassword !== accountForm.confirmPassword)}
                className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-600/50 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </button>

              {/* 2FA Section */}
              <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                <TwoFactorSettings adminId={admin?.id} />
              </div>

              {/* Security Info */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-400 font-medium">Nota de Seguridad</p>
                    <p className="text-amber-400/70 text-sm mt-1">
                      Después de cambiar tu email o contraseña, deberás hacer redeploy de la aplicación 
                      para que los cambios se reflejen en producción. Los cambios son efectivos inmediatamente 
                      en la base de datos.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
